import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser, User } from '../services/authService';
import TopBar from '../components/TopBar';
import Button from '../components/Button';

interface Business {
  id: string;
  name: string;
  address: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  logoUrl?: string;
  status?: string;
}

const Container = styled.div`
  padding: 0 24px; // Apply horizontal padding directly
  width: 100%;
  display: flex;
  flex-direction: column; // Stack TopBar and ContentWrapper vertically
  min-height: 100vh;
  // REMOVED align-items: center; // Ensure full-width layout

  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  padding-top: 20px; // Optional space below TopBar
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 16px;
  // margin-top: 20px; // Removed, rely on ContentWrapper padding
`;

const PageTitle = styled.h1`
  font-size: 38px;
  font-weight: 800;
  color: #333;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const BusinessSection = styled.div`
  margin-bottom: 40px;
  width: 100%;
`;

const BusinessCard = styled.div`
  background-color: #fff;
  border-radius: 12px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  &:last-child { margin-bottom: 0; }
`;

const BusinessInfo = styled.div`
  padding: 20px;
  display: flex;
  align-items: center;
  flex-wrap: wrap; // Allow wrapping
  gap: 10px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`;

const BusinessName = styled.h3`
  font-size: 24px;
  font-weight: 900;
  color: #333;
  margin-bottom: 4px;
`;

const AddressContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px; // Space between status and address
    flex-wrap: wrap; // Allow wrapping if space is tight
`;

const StatusLabel = styled.span`
    color: #e53935; // Red color
    font-weight: 700; // Bold
    font-size: 14px; // Slightly smaller than address
    text-transform: uppercase; // PENDING
`;

const BusinessAddress = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0;
`;

const ButtonContainer = styled.div`
  padding: 20px;
  flex-shrink: 0; // Prevent shrinking
  margin-left: 20px; // Space between info and button
`;
const EmptyState = styled.div`
  text-align: center; padding: 40px 20px; background-color: #f9f9f9;
  border-radius: 12px; margin-top: 20px;
`;
const EmptyTitle = styled.h3`
  font-size: 20px; font-weight: 600; margin-bottom: 12px; color: #333;
`;
const EmptyText = styled.p`
  font-size: 16px; color: #666; margin-bottom: 24px;
`;

const LoadingPlaceholder = styled.div`
    display: flex; justify-content: center; align-items: center;
    min-height: 200px; font-size: 18px; color: #888;
`;


const MyBusinessesPage: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserAndBusinesses = async () => {
      try {
        setLoading(true);
        const userData = await getCurrentUser();
        setUser(userData);

        if (userData?.id) {
          const managerQuery = query(collection(db, 'businesses'), where('managerId', '==', userData.id));
          const managerSnapshot = await getDocs(managerQuery);
          const managerBusinesses = managerSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Business);

          const staffQuery = query(collection(db, 'staff'), where('userId', '==', userData.id));
          const staffSnapshot = await getDocs(staffQuery);
          const staffBusinessPromises = staffSnapshot.docs.map(async (staffDoc) => {
            const businessId = staffDoc.data().businessId;
            if (!businessId) return null;
            const businessDoc = await getDoc(doc(db, 'businesses', businessId));
            return businessDoc.exists() ? { id: businessDoc.id, ...businessDoc.data() } as Business : null;
          });
          const staffBusinesses = (await Promise.all(staffBusinessPromises)).filter(b => b !== null) as Business[];

          const allBusinessesMap = new Map<string, Business>();
          managerBusinesses.forEach(b => allBusinessesMap.set(b.id, b));
          staffBusinesses.forEach(b => allBusinessesMap.set(b.id, b));

          setBusinesses(Array.from(allBusinessesMap.values()));
        } else {
          setBusinesses([]);
        }
      } catch (error) {
        console.error('Error fetching user data or businesses:', error);
        setBusinesses([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUserAndBusinesses();
  }, []);

  const handleBusinessClick = (businessId: string) => {
    navigate(`/business/${businessId}/settings`);
  };
  const handleNavigateToDashboard = () => {
    navigate('/dashboard');
  };
  const handleAddNewBusiness = () => {
    navigate('/businesses/new');
  };

  // --- Render Logic ---
  const renderLoading = () => (
    <Container>
      <ContentWrapper>
        <TopBar user={user} />
        <LoadingPlaceholder>Loading your businesses...</LoadingPlaceholder>
      </ContentWrapper>
    </Container>
  );
  const renderPageContent = () => (
    <>
      <Helmet>
        <title>{APP_NAME} - Businesses</title>
        <meta name="description" content={`${APP_NAME} - Businesses`} />
      </Helmet>
      <Container>
        <TopBar user={user} />
        <ContentWrapper>
        <HeaderSection>
          <PageTitle>Businesses</PageTitle>
          <Button text="Add" onClick={handleAddNewBusiness} bgColor="#000" textColor="#fff" width="auto" minWidth="100px" icon="+" responsive={true} />
        </HeaderSection>

        <Breadcrumbs>
          <BreadcrumbLink onClick={handleNavigateToDashboard}>My Account</BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <span>Businesses</span>
        </Breadcrumbs>

        <BusinessSection>
          {businesses.length > 0 ? (
            businesses.map((business) => (
              <BusinessCard key={business.id} onClick={() => handleBusinessClick(business.id)}>
                <BusinessInfo>
                  <TextContainer>
                    <BusinessName>{business.name}</BusinessName>
                    <AddressContainer>
                      {business.status === 'pending' && (
                        <StatusLabel>Pending</StatusLabel>
                      )}
                      {business.address && <BusinessAddress>{business.address}</BusinessAddress>}
                    </AddressContainer>
                  </TextContainer>
                </BusinessInfo>
                <ButtonContainer 
                  style={{ marginBottom: '0px' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {business.status === 'pending' && (
                    <Button 
                      text="Verify" 
                      onClick={() => navigate(`/business/${business.id}/verify`)} 
                      bgColor="#000" 
                      textColor="#fff" 
                      width="auto" 
                      minWidth="100px" 
                    />
                  )}
                </ButtonContainer>
              </BusinessCard>
            ))
          ) : (
            <EmptyState>
              <EmptyTitle>No businesses found</EmptyTitle>
              <EmptyText>Get started by adding your first business.</EmptyText>
              <Button text="Add Business" onClick={handleAddNewBusiness} bgColor="#000" textColor="#fff" width="auto" minWidth="180px" />
            </EmptyState>
          )}        </BusinessSection>
      </ContentWrapper>
    </Container>
    </>
  );

  // --- Main Return ---
  if (loading) {
    return renderLoading();
  }

  if (!user) {
    console.warn("MyBusinessesPage rendered without authenticated user.");
    return <Navigate to="/login" replace />;
  }

  return renderPageContent();
};

export default MyBusinessesPage;